<template>
  <component
    :is="!!to ? 'RouterLink' : 'button'"
    :to="to"
    :type="to ? undefined : type"
    :class="[
      getColor,
      size,
      cursor,
      rounded,
      fontWeight,
      fontSize,
      textColor,
      display,
      justify,
      textTransform,
      getDisabled(),
    ]"
    @click="handleClick"
  >
    <HealthButtonLabel
      :loading="loading"
      :icon="icon"
      :icon-class="iconClass"
      :label-class="labelClass"
      :loading-icon-class="loadingIconClass"
      :label="label"
    >
      <slot />
    </HealthButtonLabel>
  </component>
</template>

<script setup lang="ts">
  import { computed } from "vue";
  import type { ButtonProps } from "./props.ts";
  import HealthButtonLabel from "@/shared/components/utils/button/HealthButtonLabel.vue";

  const emit = defineEmits(["action"]);

  type Props = Omit<ButtonProps, "">;

  const props = withDefaults(defineProps<Props>(), {
    color: "primary",
    fontWeight: "font-medium",
    rounded: "rounded-md",
    type: "button",
    size: "px-2 py-2",
    fontSize: "text-sm 4k:text-[17px]",
    display: "inline-flex",
    textColor: "text-white",
    justify: "justify-center",
    iconClass: "min-w-[1.4rem] min-h-[1.4rem]",
    loadingIconClass: "min-w-[1.4rem] min-h-[1.4rem]",
    labelClass: "hidden md:block",
    noFocus: false,
    colorClass: "",
  });

  function getDisabled() {
    return props.disabled || props.loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer";
  }

  function handleClick(event: MouseEvent) {
    if (props.to) return;

    if (props.disabled || props.loading) {
      event.preventDefault();
      return;
    }

    emit("action", event);
  }

  const getColor = computed(() => {
    const { color, disabled, loading, noFocus } = props;
    const canFocus = !(disabled || loading || noFocus);

    if (props.colorClass) return props.colorClass;

    switch (color) {
      case "primary":
        return [
          "bg-primary-500 hover:bg-primary-700 dark:bg-primary-900 dark:hover:bg-primary-800",
          canFocus
            ? "focus:ring-2 focus:ring-primary-200 focus:outline-none dark:focus:ring-primary-800"
            : "",
        ];
      case "red":
        return [
          "bg-red-600 hover:bg-red-700 dark:bg-red-900 dark:hover:bg-red-800",
          canFocus
            ? "focus:ring-2 focus:ring-red-200 focus:outline-none dark:focus:ring-red-800"
            : "",
        ];
      case "gray":
        return [
          "bg-gray-600 hover:bg-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800",
          canFocus
            ? "focus:ring-2 focus:ring-gray-200 focus:outline-none dark:focus:ring-gray-800"
            : "",
        ];
      case "light":
        return [
          "bg-transparent border border-gray-200 hover:bg-gray-100 dark:bg-transparent dark:border-gray-700 dark:hover:bg-gray-800 dark:hover:border-gray-700 transition-colors duration-200",
          canFocus
            ? "focus:ring-2 focus:ring-gray-100 focus:outline-none dark:focus:ring-gray-800"
            : "",
        ];
      case "transparent":
        return ["bg-transparent", canFocus ? "focus:outline-none" : ""];
      default:
        return ["", ""];
    }
  });
</script>
